// Add-to-cart for the cart drawer's "You may also like" block (snippets/cart-upsell.liquid).
// It uses the same cart events the bundle selector does, so the drawer, header count and the
// free-bands guard (gift-guard.js) all update exactly as they do for any other add.

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

async function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    return cartItems.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

const MAX_QTY = 6;

// Bundle & Save tiers (same as the store's automatic discounts): 2 -> 25% off, 3+ -> 35% off each.
const tierPercent = (total) => (total >= 3 ? 35 : total === 2 ? 25 : 0);

const formatMoney = (cents, currency) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(cents / 100);

const getQty = (item) => Number(item.querySelector('[data-upsell-qty]')?.textContent) || 1;

/** Keeps the stepper, price and hook of one upsell row in step with the quantity chosen. */
function renderRow(item) {
  const qty = getQty(item);
  const decrease = item.querySelector('[data-upsell-qty-step="-1"]');
  const increase = item.querySelector('[data-upsell-qty-step="1"]');
  if (decrease) decrease.disabled = qty <= 1;
  if (increase) increase.disabled = qty >= MAX_QTY;

  const unit = Number(item.dataset.unitCents);
  const compare = Number(item.dataset.compareCents);
  const total = Number(item.dataset.existing || 0) + qty;
  const pct = tierPercent(total);
  const price = unit - Math.floor((unit * pct) / 100); // Shopify floors the discount per unit
  const currency = item.closest('[data-currency]')?.dataset.currency;

  const priceEl = item.querySelector('[data-upsell-price]');
  if (priceEl) {
    const was = Math.max(compare, pct ? unit : 0);
    priceEl.innerHTML = `${was > price ? `<s>${formatMoney(was, currency)}</s> ` : ''}${formatMoney(price, currency)}`;
  }

  const hookEl = item.querySelector('[data-upsell-hook]');
  if (hookEl && item.dataset.kind === 'trainer') {
    hookEl.innerHTML =
      total >= 3
        ? '🎁 <strong>35% off</strong> each + <strong>FREE bands</strong>'
        : '🔥 <strong>25% off</strong> each · add 2 more for <strong>35% off + FREE bands</strong>';
  }
}

async function addToCart(button) {
  const variantId = Number(button.dataset.variantId);
  if (!variantId || button.disabled) return;

  const item = button.closest('[data-upsell-item]');
  const quantity = item ? getQty(item) : 1;

  const label = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding…';

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    }).then((result) => result.json());

    if (response.status) throw new Error(response.message || 'Add to cart failed');

    const cart = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'cart-upsell', sourceId: '', didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    button.dispatchEvent(
      new CartErrorEvent({ error: error && error.message ? error.message : 'Add to cart failed', code: 'INVALID' })
    );
    // On success the drawer re-renders (this button disappears); only a failure needs resetting.
    button.disabled = false;
    button.textContent = label;
  }
}

if (!window.__cartUpsellBound) {
  window.__cartUpsellBound = true;
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-upsell-qty-step]');
    if (step) {
      event.preventDefault();
      const item = step.closest('[data-upsell-item]');
      const valueEl = item?.querySelector('[data-upsell-qty]');
      if (valueEl) {
        const next = Math.min(MAX_QTY, Math.max(1, getQty(item) + Number(step.dataset.upsellQtyStep)));
        valueEl.textContent = String(next);
        renderRow(item);
      }
      return;
    }

    const button = event.target.closest('[data-cart-upsell-add]');
    if (button) {
      event.preventDefault();
      addToCart(button);
    }
  });
}
